/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 28/07/2026
  ========================================================================
  gro-blog-index.js - progressively refresh Blog filters and pagination.

  OWNERSHIP
    Owns same-origin navigation initiated by links carrying
    data-gro-blog-navigation inside a Gro Blog Index. It does not calculate
    filter results, alter article data, or replace the index header.

  GRO MODULE CONTRACT - read before editing
    Shopify remains the source of truth. Fetch the selected native URL, parse
    the matching section instance, and replace only data-gro-blog-results.
    Preserve functional href attributes as the no-JavaScript and error
    fallback. Update browser history only after a successful replacement;
    restore results on Back/Forward navigation. Abort superseded requests,
    expose aria-busy during loading, use modern vanilla JavaScript, and add no
    runtime dependencies. Log every change in the module CHANGELOG.md.

  DEPENDENCIES
    Markup hooks from sections/gro-blog-index.liquid,
    snippets/gro-blog-index-filters.liquid, and
    snippets/gro-blog-index-pagination.liquid. Browser Fetch, DOMParser,
    AbortController, History and CustomEvent APIs.

  DELIBERATE EXEMPTIONS
    AGENTS.md Anchor ID, shell and Layout & spacing rules do not apply to a
    behaviour-only asset.
  ========================================================================
*/

(() => {
  if (window.groBlogIndexNavigationBound) return;
  window.groBlogIndexNavigationBound = true;

  const rootSelector = "[data-gro-blog-index]";
  const navigationSelector = "a[data-gro-blog-navigation]";
  const resultsSelector = "[data-gro-blog-results]";
  const requests = new WeakMap();

  const getIncomingResults = (html, sectionId) => {
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const incomingSection = documentFragment.getElementById(`shopify-section-${sectionId}`);
    return incomingSection?.querySelector(resultsSelector) ?? null;
  };

  const refreshResults = async (root, url, options = {}) => {
    const currentResults = root.querySelector(resultsSelector);
    const sectionId = root.dataset.sectionId;

    if (!currentResults || !sectionId) {
      window.location.assign(url.href);
      return;
    }

    requests.get(root)?.abort();
    const controller = new AbortController();
    requests.set(root, controller);
    root.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(url.href, {
        signal: controller.signal,
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) throw new Error(`Blog request failed with ${response.status}`);

      const incomingResults = getIncomingResults(await response.text(), sectionId);
      if (!incomingResults) throw new Error("Blog results region was not found");

      currentResults.replaceWith(incomingResults);

      if (options.pushHistory) {
        window.history.pushState(
          { ...window.history.state, groBlogIndex: sectionId },
          "",
          url.href
        );
      }

      if (options.focusFilter) {
        root.querySelector("[data-gro-blog-filter][aria-current='page']")?.focus({
          preventScroll: true
        });
      }

      if (options.scrollToResults) {
        root.querySelector(resultsSelector)?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start"
        });
      }

      root.dispatchEvent(
        new CustomEvent("gro:blog-index:updated", {
          bubbles: true,
          detail: { url: url.href }
        })
      );
    } catch (error) {
      if (error.name === "AbortError") return;
      window.location.assign(url.href);
    } finally {
      if (requests.get(root) === controller) {
        requests.delete(root);
        root.removeAttribute("aria-busy");
      }
    }
  };

  document.addEventListener("click", (event) => {
    const link = event.target.closest(navigationSelector);
    const root = link?.closest(rootSelector);

    if (
      !link ||
      !root ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === "_blank"
    ) {
      return;
    }

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    refreshResults(root, url, {
      pushHistory: true,
      focusFilter: link.hasAttribute("data-gro-blog-filter"),
      scrollToResults: link.hasAttribute("data-gro-blog-page")
    });
  });

  window.addEventListener("popstate", () => {
    document.querySelectorAll(rootSelector).forEach((root) => {
      const blogUrl = new URL(root.dataset.blogUrl, window.location.origin);
      const isBlogRoute =
        window.location.pathname === blogUrl.pathname ||
        window.location.pathname.startsWith(`${blogUrl.pathname}/tagged/`);

      if (isBlogRoute) {
        refreshResults(root, new URL(window.location.href), {
          pushHistory: false,
          focusFilter: false,
          scrollToResults: false
        });
      }
    });
  });
})();
